import {Component, EventEmitter, forwardRef, inject, InjectionToken, Input, OnInit, Output} from '@angular/core';
import {FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, tap} from 'rxjs';
import {BaseCustomInputComponent} from '../base-custom-input.component';
import {NgClass, NgIf} from '@angular/common';
import {MatSliderModule} from '@angular/material/slider';
import {MatTooltipModule} from '@angular/material/tooltip';


export const REAFFIRM_ON_RELEASE = new InjectionToken<boolean>('REAFFIRM_ON_RELEASE');

@Component({
  selector: 'app-slider-input',
  standalone: true,
  template: `
    <div class="row align-items-center no-gutter mt-2">
      <div class="col-4 pe-1" *ngIf="label && !isIcon">
        {{label}}
      </div>
      <div class="col-2 justify-content-center align-items-center d-flex" [matTooltip]="placeholder"  *ngIf="label && isIcon">
        <span class="material-symbols-rounded pe-1"><label>{{label}}</label></span>
      </div>
      <div [ngClass]="{'col-8': label !== undefined && label !== '' && !isIcon, 'col-10': label !== '' && isIcon}" class="ps-0 pe-4">
        <mat-slider
          class="w-100"
          [max]="max"
          [disableRipple]="true"
          [discrete]="discrete"
          [min]="min"
          [step]="step"
          [displayWith]="formatValue"
          [showTickMarks]="showTickMarks">
          <input matSliderThumb
                 [formControl]="formControl"
                 [value]="convertedValue"
                 (dragStart)="onDragStart()"
                 (dragEnd)="onDragEnd()"
                 (input)="onImmediateChange($event)">
        </mat-slider>
      </div>
    </div>

  `,
  styleUrls: ['./slider-input.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, NgClass, NgIf, MatSliderModule, MatTooltipModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SliderInputComponent),
      multi: true,
    },
  ],
})
export class SliderInputComponent extends BaseCustomInputComponent<number> implements OnInit  {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() value: number | string = 0;
  @Input() isIcon = false;
  @Input() min = 0;
  @Input() max = 16;
  @Input() step = 1;
  @Input() discrete = true;
  @Input() showTickMarks = true;
  @Input() displayMultiplier = 1;
  @Input() valueSuffix = '';

  readonly formatValue = (value: number): string => `${value}${this.valueSuffix}`;
  @Output() immediateValueChange = new EventEmitter<number>();

  // Track if we're in initialization phase
  private initializing = true;

  // Track the original internal value
  private internalValue = 0;

  // Add debugging to track value changes
  private _previousValues: {formControl: any, value: any, internalValue: any}[] = [];

  // Override writeValue to handle the conversion correctly
  override writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.formControl.setValue(null, { emitEvent: false });
      this.initializing = false;
      return;
    }

    // Special case for scale of 0
    const numValue = typeof value === 'string' ? Number(value) : value;

    // Store the internal value for future reference
    this.internalValue = numValue;

    // Calculate displayed value (must explicitly check for 0)
    const displayValue = numValue === 0 ? 0 : numValue * this.displayMultiplier;

    // Log for debugging
    this._previousValues.push({
      formControl: this.formControl.value,
      value: value,
      internalValue: this.internalValue
    });

    // Update the form control without triggering change events
    this.formControl.setValue(displayValue, { emitEvent: false });

    // Mark initialization as complete
    this.initializing = false;
  }

  override ngOnInit(): void {
    // When user interacts with the slider, convert display value back to internal value
    this.formControl.valueChanges
      .pipe(
        debounceTime(200),
        tap(displayedValue => {
          if (this.initializing) {
            return; // Skip during initialization phase
          }

          // Log current state for debugging
          this._previousValues.push({
            formControl: displayedValue,
            value: this.value,
            internalValue: this.internalValue
          });

          // Strict type checking for zero values
          // This is critical for the scale property
          if (displayedValue === 0) {
            this.internalValue = 0;
            this.onChange(0);
            return;
          }

          // For non-zero values, divide by the multiplier
          const numDisplayValue =
            typeof displayedValue === 'string' ?
            Number(displayedValue) : displayedValue;

          // Convert display value to internal value
          const internalValue = numDisplayValue / this.displayMultiplier;

          // Store and emit the value
          this.internalValue = internalValue;
          this.onChange(internalValue);
        }),
      )
      .subscribe();
  }

  private readonly reaffirmOnRelease = inject(REAFFIRM_ON_RELEASE, {optional: true}) ?? false;
  private dragStartValue: unknown;

  onDragStart(): void {
    this.dragStartValue = this.formControl.value;
  }

  onDragEnd(): void {
    if (!this.reaffirmOnRelease || this.initializing) return;
    const displayedValue = Number(this.formControl.value);
    if (Number(this.dragStartValue) !== displayedValue || Number.isNaN(displayedValue)) return;
    this.onChange(displayedValue === 0 ? 0 : displayedValue / this.displayMultiplier);
  }

  onImmediateChange(event: Event): void {
    if (this.initializing) return;

    const target = event.target as HTMLInputElement;
    const displayedValue = Number(target.value);

    // Convert display value to internal value using the same logic as the debounced handler
    let internalValue: number;

    if (displayedValue === 0) {
      internalValue = 0;
    } else {
      internalValue = displayedValue / this.displayMultiplier;
    }

    // Emit the immediate change without debounce
    this.immediateValueChange.emit(internalValue);
  }

  // Used by the slider to determine what value to display in the UI
  get convertedValue(): number {
    // After initialization, always use the form control value
    // This prevents the @Input() value from overriding user changes
    if (!this.initializing && this.formControl.value !== null && this.formControl.value !== undefined) {
      return this.formControl.value;
    }

    // During initialization, use the @Input() value if available
    if (this.value !== null && this.value !== undefined) {
      const numValue = typeof this.value === 'string' ? Number(this.value) : this.value;
      if (numValue === 0) return 0;
      return numValue * this.displayMultiplier;
    }

    // Fallback to form control value
    if (this.formControl.value !== null && this.formControl.value !== undefined) {
      if (this.formControl.value === 0) return 0;
      return this.formControl.value;
    }

    // Default to 0
    return 0;
  }
}
